import { useEffect } from "react";

import { useHead } from "@unhead/react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { buildUrl } from "@/components/helpers/url-utils";
import { isInWorkspace } from "@/components/helpers/workspace/permissions";
import { Box } from "@/components/layout/flexbox";
import { isXsToLgQuery } from "@/components/layout/Media";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { NotFound } from "@/components/pages_public/404/NotFound";
import { AffirmTag } from "@/components/pieces/buy-credits/AffirmTag";
import { PaymentForm } from "@/components/pieces/buy-credits/PaymentForm";
import { usePendingOrder } from "@/components/pieces/buy-credits/usePendingOrder";
import { BackButton } from "@/components/pieces/onboarding/BackButton";
import { Header } from "@/components/pieces/onboarding/Header";
import { PageTitle } from "@/components/pieces/onboarding/PageTitle";
import {
  LIGHTEST_BACKGROUND,
  SITE_NAME,
  STANDARD_NAV_HEIGHT,
} from "@/css/constants";

import { CostExplainer } from "./CostExplainer";

interface AuthCheckoutProps {
  id: string;
  customerId?: string;
}

function AuthCheckout({ id, customerId }: AuthCheckoutProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, { syncing, setOrder }] = usePendingOrder({
    id,
    withCoachInfo: true,
    customerId,
  });

  const onOrderConfirmed = (orderId?: string) => {
    const back = searchParams.get("back");
    const nextStepsUrl = buildUrl(`/checkout/${orderId}/next-steps`, {
      data: {
        back,
        clientId: customerId,
      },
    });
    navigate(nextStepsUrl);
  };

  if (!syncing && !order) {
    onOrderConfirmed(id);
  }

  const title = (
    <PageTitle
      title="Checkout to confirm your order"
      subtitle="We are excited to get started!"
    />
  );

  const hasIntro = !!order?.orderItems?.filter(
    (oi) => oi?.product?.introProduct,
  ).length;

  return (
    <Box p={3}>
      {title}
      {hasIntro && !IS_EXEC ? <CostExplainer /> : null}
      <Box mt={4}>
        <PaymentForm
          order={order}
          onOrderConfirmed={onOrderConfirmed}
          setOrder={setOrder}
          ctaVerb="Confirm Order"
          // All workspace member cannot buy credits when checkout
          allowBuyCredits={!isInWorkspace()}
          syncing={syncing}
          customerId={customerId}
          loading={false}
        />
      </Box>
    </Box>
  );
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const { id, userId } = useParams();
  const [searchParams] = useSearchParams();
  const back = searchParams.get("back");

  useEffect(() => {
    if (!id) {
      navigate("/");
    }
  }, [id, navigate]);

  useHead({
    bodyAttrs: { style: { backgroundColor: LIGHTEST_BACKGROUND } },
  });

  if (!id) {
    return <NotFound />;
  }

  return (
    <Authenticate maxWidth="100%" layout="compact" showMessageBar={false}>
      <SEOHead
        title={`Buy Coaching Services - ${SITE_NAME}`}
        description="Buy sessions to help find your direction, map out your strategy, and train for interviews"
        canonicalPath={`/checkout/${id}`}
        image="https://placement-pub.s3.us-east-2.amazonaws.com/buy-sessions-meta.png"
      />
      <Header />
      <Box mt={STANDARD_NAV_HEIGHT} css={{ position: "relative" }}>
        {back && (
          <BackButton
            onClick={() => {
              navigate(back);
            }}
            mediaQuery={isXsToLgQuery}
          />
        )}
        <Box maxWidth={800} mx="auto" pb={60}>
          <AuthCheckout id={id} customerId={userId} />
        </Box>
      </Box>
      <AffirmTag />
    </Authenticate>
  );
}
